/**
 * Deterministic, on-brand visuals for recipes without a photo, and colours
 * for cuisine/course badges (visual refresh 2026-10). Pure — no React — so
 * the choices are unit-tested.
 *
 * Placeholder palettes use only brand colours (teal, green, orange, cream and
 * their soft tints). `icon` is the foreground colour for the course glyph; it
 * is decorative (the image is aria-hidden), but still picked for ≥3:1 against
 * the gradient so it reads as a graphic.
 */
import type { Course, Cuisine } from '@fewerbags/schema';

export type PlaceholderPaletteKey = 'teal' | 'green' | 'orange' | 'mint' | 'peach' | 'sage';

export interface PlaceholderPalette {
  from: string;
  to: string;
  /** Pattern marks (dots / stripes / rings). */
  pattern: string;
  /** Course glyph stroke. */
  icon: string;
  /** Disc behind the glyph. */
  disc: string;
}

export const PLACEHOLDER_PALETTES: Record<PlaceholderPaletteKey, PlaceholderPalette> = {
  teal: { from: '#1c5f53', to: '#2e7b6b', pattern: '#aecc5e', icon: '#fef7e5', disc: '#154a41' },
  green: { from: '#aecc5e', to: '#cfe29a', pattern: '#1c5f53', icon: '#1c5f53', disc: '#eaf2d3' },
  orange: { from: '#fe9e49', to: '#ffc187', pattern: '#fef7e5', icon: '#154a41', disc: '#ffe7d0' },
  mint: { from: '#e2ece9', to: '#fdfdf7', pattern: '#1c5f53', icon: '#1c5f53', disc: '#fdfdf7' },
  peach: { from: '#ffe7d0', to: '#fef7e5', pattern: '#fe9e49', icon: '#96500d', disc: '#fdfdf7' },
  sage: { from: '#eaf2d3', to: '#e2ece9', pattern: '#52701b', icon: '#1c5f53', disc: '#fdfdf7' },
};

export type PlaceholderPattern = 'dots' | 'stripes' | 'rings';
export type PlaceholderGlyph =
  'pot' | 'leaf' | 'bowl' | 'bread' | 'cup' | 'cake' | 'egg' | 'drop' | 'skewer';

const COURSE_STYLE: Record<Course, { palette: PlaceholderPaletteKey; glyph: PlaceholderGlyph }> = {
  main: { palette: 'teal', glyph: 'pot' },
  side: { palette: 'green', glyph: 'leaf' },
  salad: { palette: 'sage', glyph: 'leaf' },
  soup: { palette: 'orange', glyph: 'bowl' },
  bread: { palette: 'peach', glyph: 'bread' },
  sauce: { palette: 'peach', glyph: 'drop' },
  appetizer: { palette: 'mint', glyph: 'skewer' },
  breakfast: { palette: 'orange', glyph: 'egg' },
  dessert: { palette: 'peach', glyph: 'cake' },
  snack: { palette: 'green', glyph: 'skewer' },
  drink: { palette: 'mint', glyph: 'cup' },
};

const PALETTE_ORDER: readonly PlaceholderPaletteKey[] = [
  'teal',
  'green',
  'orange',
  'mint',
  'peach',
  'sage',
];
const PATTERNS: readonly PlaceholderPattern[] = ['dots', 'stripes', 'rings'];

/** Small stable string hash (FNV-1a, 32-bit). */
export function hashString(value: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export interface PlaceholderStyle {
  palette: PlaceholderPalette;
  paletteKey: PlaceholderPaletteKey;
  pattern: PlaceholderPattern;
  glyph: PlaceholderGlyph;
}

/**
 * The placeholder for a recipe: colour and glyph from its first course (else
 * a hash of its first cuisine, else of its title); the pattern varies by
 * title so neighbouring cards of the same course still differ.
 */
export function placeholderStyle(
  seed: string,
  courses: readonly Course[] = [],
  cuisines: readonly Cuisine[] = [],
): PlaceholderStyle {
  const h = hashString(seed);
  const course = courses[0];
  let paletteKey: PlaceholderPaletteKey;
  let glyph: PlaceholderGlyph;
  if (course) {
    ({ palette: paletteKey, glyph } = COURSE_STYLE[course]);
  } else {
    const ch = cuisines[0] ? hashString(cuisines[0]) : h;
    paletteKey = PALETTE_ORDER[ch % PALETTE_ORDER.length] ?? 'teal';
    glyph = 'pot';
  }
  return {
    palette: PLACEHOLDER_PALETTES[paletteKey],
    paletteKey,
    pattern: PATTERNS[(h >>> 4) % PATTERNS.length] ?? 'dots',
    glyph,
  };
}

// ------------------------------------------------------------------ badges --

/** Tailwind classes for a course badge, matching the placeholder palette family. */
export const COURSE_BADGE: Record<PlaceholderPaletteKey, string> = {
  teal: 'bg-brand text-white',
  green: 'bg-accent text-ink',
  orange: 'bg-orange text-brand-dark',
  mint: 'bg-brand-soft text-brand',
  peach: 'bg-orange-soft text-orange-ink',
  sage: 'bg-accent-soft text-accent-ink',
};

export function courseBadgeClass(course: Course): string {
  return COURSE_BADGE[COURSE_STYLE[course].palette];
}

export type CuisineRegion = 'americas' | 'europe' | 'asia' | 'other';

const REGION_OF: Partial<Record<Cuisine, CuisineRegion>> = {};
const REGIONS: Record<CuisineRegion, readonly Cuisine[]> = {
  americas: [
    'american',
    'bbq',
    'southern',
    'cajun',
    'tex-mex',
    'mexican',
    'latin-american',
    'caribbean',
  ],
  europe: ['italian', 'french', 'spanish', 'mediterranean', 'greek', 'middle-eastern'],
  asia: ['indian', 'asian', 'chinese', 'japanese', 'korean', 'thai', 'vietnamese', 'hawaiian'],
  other: ['fusion', 'other'],
};
for (const [region, keys] of Object.entries(REGIONS) as Array<[CuisineRegion, Cuisine[]]>) {
  for (const key of keys) REGION_OF[key] = region;
}

export function cuisineRegion(cuisine: Cuisine): CuisineRegion {
  return REGION_OF[cuisine] ?? 'other';
}

/** Cuisine badges: outlined, one colour family per region (all AA on their fill). */
export const CUISINE_BADGE: Record<CuisineRegion, string> = {
  americas: 'border border-orange/60 bg-orange-soft/60 text-orange-ink',
  europe: 'border border-brand/30 bg-surface text-brand',
  asia: 'border border-accent bg-accent-soft/60 text-accent-ink',
  other: 'border border-line bg-cream text-ink',
};

export function cuisineBadgeClass(cuisine: Cuisine): string {
  return CUISINE_BADGE[cuisineRegion(cuisine)];
}
